import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { getPortfolioItems } from '../../lib/api'
import GlassCard from '../ui/GlassCard'
import SectionTitle from '../ui/SectionTitle'
import ScrollReveal from '../ui/ScrollReveal'
import LoadingSpinner from '../ui/LoadingSpinner'
import GridGlowBackground from '../ui/grid-glow-background'
import type { PortfolioItem } from '../../types'

const categories = [
  { value: 'all', label: 'All' },
  { value: 'web-design', label: 'Web Design' },
  { value: 'development', label: 'Development' },
  { value: 'ecommerce', label: 'eCommerce' },
  { value: 'seo', label: 'SEO' },
  { value: 'branding', label: 'Branding' },
] as const

export default function PortfolioGrid() {
  const [data, setData] = useState<PortfolioItem[]>([])
  const [filter, setFilter] = useState<string>('all')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getPortfolioItems(filter as PortfolioItem['category'] | 'all').then(
      (res) => {
        setData(res)
        setLoading(false)
      }
    )
  }, [filter])

  return (
    <GridGlowBackground
      backgroundColor="#030712"
      gridColor="rgba(255,255,255,0.02)"
      gridSize={60}
      glowColors={['#00d4ff', '#7c3aed', '#00d4ff']}
      glowCount={6}
      className="py-24"
    >
      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          label="Our Work"
          title="Featured Projects"
          subtitle="A selection of our finest work — each project crafted with precision, passion, and purpose."
        />

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map((cat) => (
            <button
              key={cat.value}
              onClick={() => {
                setLoading(true)
                setFilter(cat.value)
              }}
              className={`px-5 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                filter === cat.value
                  ? 'bg-gradient-to-r from-electric-blue to-electric-purple text-white shadow-lg shadow-electric-blue/20'
                  : 'text-gray-400 bg-white/5 border border-white/5 hover:border-white/20 hover:text-white'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <LoadingSpinner text="Loading projects..." />
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={filter}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {data.map((item, i) => (
                <ScrollReveal key={item.id} delay={i * 0.05} direction="up">
                  <GlassCard className="group relative overflow-hidden h-full flex flex-col">
                    <div className="relative aspect-[4/3] shrink-0 overflow-hidden">
                      <img
                        src={item.thumbnail}
                        alt={item.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/20 to-transparent" />
                      <div className="absolute bottom-4 left-4">
                        <span className="px-3 py-1 text-xs font-medium text-white bg-electric-blue/80 rounded-full backdrop-blur-sm capitalize">
                          {item.category.replace('-', ' ')}
                        </span>
                      </div>
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute inset-0 flex items-center justify-center bg-gray-950/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        >
                          <span className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-full bg-gradient-to-r from-electric-blue to-electric-purple shadow-lg shadow-electric-blue/30 translate-y-4 group-hover:translate-y-0 transition-all duration-500">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            View Project
                          </span>
                        </a>
                      )}
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      {item.url ? (
                        <a href={item.url} target="_blank" rel="noopener noreferrer" className="group/title">
                          <h3 className="text-lg font-bold text-white group-hover/title:text-electric-blue transition-colors duration-300">
                            {item.title}
                          </h3>
                        </a>
                      ) : (
                        <h3 className="text-lg font-bold text-white">
                          {item.title}
                        </h3>
                      )}
                      <p className="mt-2 text-sm text-gray-400 line-clamp-2">
                        {item.description}
                      </p>
                      <div className="mt-4 flex flex-wrap gap-1.5 flex-1 content-start">
                        {item.technologies.slice(0, 3).map((tech) => (
                          <span
                            key={tech}
                            className="px-2.5 py-1 text-xs font-medium text-gray-500 bg-white/5 rounded-md"
                          >
                            {tech}
                          </span>
                        ))}
                        {item.technologies.length > 3 && (
                          <span className="px-2.5 py-1 text-xs font-medium text-gray-500 bg-white/5 rounded-md">
                            +{item.technologies.length - 3}
                          </span>
                        )}
                      </div>
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="self-end inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-white rounded-full bg-gradient-to-r from-electric-blue to-electric-purple hover:shadow-lg hover:shadow-electric-blue/25 transition-all duration-300 hover:scale-105 hover:-translate-y-0.5"
                        >
                          View Demo
                          <svg className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      )}
                    </div>
                  </GlassCard>
                </ScrollReveal>
              ))}
            </motion.div>
          </AnimatePresence>
        )}
        </div>
      </section>
    </GridGlowBackground>
  )
}
